import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Navbar from "./navbar";
import { UserIcon } from "../shared/icons";
import { useAuth } from "../contexts/AuthContext";
import { LOCALES, LOCALE_META, type AppLocale } from "../i18n/locales";
import {
  LocalizedLink,
  useSwitchLocale,
  useAppLocale,
} from "../i18n/LocaleRouting";

const Header: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const switchLocale = useSwitchLocale();
  const currentLocale = useAppLocale();
  const { user, loading, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen && !langOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuOpen && !menuRef.current?.contains(target)) {
        setMenuOpen(false);
      }
      if (langOpen && !langRef.current?.contains(target)) {
        setLangOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuOpen, langOpen]);

  const displayName =
    user?.displayName?.trim() ||
    user?.email?.split("@")[0] ||
    t("header.account");

  return (
    <>
      <div className="w-full flex justify-center md:border-b-[1px] bg-black">
        <div className="w-full max-w-[1600px] md:h-[84px] h-[74px] px-4 sm:px-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-x-2 min-w-0 shrink">
            <LocalizedLink
              to="/"
              className="cursor-pointer flex items-center gap-x-0 shrink-0"
            >
              <img
                src="/icon/tcg-logo.png"
                alt={t("brand.logoAlt")}
                className="md:w-[42px] w-9 md:h-[42px] h-9 shrink-0"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
              <span className="md:text-[22px] text-[18px] font-bold text-[#fff] whitespace-nowrap">
                {t("brand.name")}
              </span>
            </LocalizedLink>
            <div className="ml-2 shrink-0">
              <Navbar />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 text-sm min-w-0 shrink">
            <div className="relative" ref={langRef}>
              <button
                type="button"
                aria-label={t("header.language")}
                aria-expanded={langOpen}
                onClick={() => {
                  setLangOpen((open) => !open);
                  setMenuOpen(false);
                }}
                className="rounded-full bg-white/15 hover:bg-white/20 px-2.5 py-1.5 text-white text-xs sm:text-sm font-medium"
              >
                {LOCALE_META[currentLocale].label}
              </button>
              {langOpen ? (
                <div className="absolute right-0 mt-2 min-w-[4rem] rounded-lg bg-white/90 backdrop-blur-md shadow-lg border border-[#eee] overflow-hidden z-50">
                  {LOCALES.map((loc: AppLocale) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => {
                        setLangOpen(false);
                        if (loc !== currentLocale) switchLocale(loc);
                      }}
                      className={`block w-full text-left px-3 py-2.5 text-xs hover:bg-[#f7f7f7]/45 hover:text-[#F05A2A] ${
                        loc === currentLocale
                          ? "font-semibold text-[#F05A2A]"
                          : "text-[#222]"
                      }`}
                    >
                      {LOCALE_META[loc].label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {loading ? (
              <div className="h-8 w-20 rounded-full bg-white/10 animate-pulse" />
            ) : user ? (
              <div className="relative min-w-0 max-w-full" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen((open) => !open);
                    setLangOpen(false);
                  }}
                  className="flex max-w-full items-center gap-2 rounded-full bg-white/15 hover:bg-white/20 pl-1 pr-2 sm:pr-3 py-1 text-white min-w-0"
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt=""
                      className="w-7 h-7 rounded-full object-cover shrink-0"
                      referrerPolicy="no-referrer"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  ) : (
                    <span className="w-7 h-7 rounded-full bg-[#F05A2A] flex items-center justify-center shrink-0">
                      <UserIcon size={14} color="#fff" />
                    </span>
                  )}
                  <span className="min-w-0 max-w-[72px] sm:max-w-[100px] md:max-w-[140px] truncate font-medium text-xs sm:text-sm">
                    {displayName}
                  </span>
                </button>

                {menuOpen && (
                  <div className="min-w-[7.5rem] absolute right-0 mt-2 rounded-lg bg-white/75 backdrop-blur-md shadow-lg border border-[#eee] overflow-hidden z-50">
                    <LocalizedLink
                      to="/profile"
                      onClick={() => setMenuOpen(false)}
                      className="block w-full text-left px-3 py-2.5 text-xs text-[#222] text-nowrap hover:bg-[#f7f7f7]/45 hover:text-[#F05A2A]"
                    >
                      {t("header.personalCenter")}
                    </LocalizedLink>
                    <button
                      type="button"
                      onClick={async () => {
                        setMenuOpen(false);
                        await signOut();
                      }}
                      className="w-full text-left px-3 py-2.5 text-xs text-[#222] hover:bg-[#f7f7f7]/45 hover:text-[#F05A2A] border-t border-[#f0f0f0]"
                    >
                      {t("header.signOut")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <LocalizedLink
                to="/auth"
                state={{ from: location.pathname + location.search }}
              >
                <button className="bg-[#F05A2A] text-white px-2 py-1 rounded-full flex items-center gap-x-1.5">
                  <UserIcon size={16} color="#fff" />
                  {t("header.login")}
                </button>
              </LocalizedLink>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
